import type { Epic } from 'redux-observable';
import { EMPTY, from, of } from 'rxjs';
import { catchError, filter, mergeMap } from 'rxjs/operators';
import type { Action } from '@reduxjs/toolkit';

import { locales } from '@/modules/common/constants/languages';

import { TranslationMapType } from '@/modules/common/types/i18n';

import { setTranslationsAction } from '../slices/i18n';
import logger from '@/modules/common/helpers/logger';
import { translateLanguageSelector, translationsSelector } from '@/modules/common/redux/selectors/i18n';
import { setLanguageAction } from '@/modules/common/redux/slices/common';

const i18nEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action): action is ReturnType<typeof setLanguageAction> => action.type === setLanguageAction.type),
    mergeMap((action: ReturnType<typeof setLanguageAction>) => {
      const lang = action.payload;

      const translateLanguage = translateLanguageSelector(state$.value);
      const translations = translationsSelector(state$.value);

      if (translateLanguage !== lang) {
        return from(locales[lang]()).pipe(
          mergeMap((module) =>
            of(
              setTranslationsAction({
                translateLanguage: lang,
                translations: module.default as TranslationMapType,
              }),
            ),
          ),
          catchError((err) => {
            logger.error(`[i18n] Failed to load language "${lang}":`, err);
            return of(
              setTranslationsAction({
                translateLanguage,
                translations: { ...translations },
              }),
            );
          }),
        );
      }

      return EMPTY;
    }),
  );

export default i18nEpic;
